import { ChangeEvent, useState } from 'react';

import { Box, FormControl, FormControlLabel, Input, Radio, RadioGroup } from '@mui/material';
import { ClassNameMap } from '@mui/styles';

import { Condition, TableFilter } from '../../Table.types';

const getActiveCondition = (
  filters,
  filterType,
  columnFilterConditions?: Array<Condition>,
): string => {
  const filtersKeys = Object.keys(filters);

  const validConditionKeys = columnFilterConditions?.map(condition => condition.key) || [];
  const activeCondition = filtersKeys.find(key => filters[key] && validConditionKeys.includes(key));

  if (activeCondition) {
    return activeCondition;
  }

  if (filtersKeys.length === 0 && columnFilterConditions?.length === 1) {
    return columnFilterConditions[0].key;
  }

  return filterType === 'text' ? 'contains' : 'equals';
};

type TextFilterControlProps = {
  filter: TableFilter;
  activeFilters: Record<string, string>;
  columnFilterConditions?: Array<Condition>;
  classes: ClassNameMap;
  onFilterChange: (condition: string, value: any) => any;
  onClose: (e: any) => void;
  placeholder: string;
  radioButtonColor?: 'default' | 'primary' | 'secondary';
};

export const TextFilterControl = ({
  filter,
  activeFilters,
  columnFilterConditions,
  classes,
  onFilterChange,
  onClose,
  placeholder,
  radioButtonColor = 'primary',
}: TextFilterControlProps) => {
  const [activeCondition, setActiveCondition] = useState(
    getActiveCondition(activeFilters, filter.type, columnFilterConditions),
  );

  const handleConditionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newCondition = e.target.value;
    const currentValue = activeFilters[activeCondition];
    onFilterChange(activeCondition, '');
    onFilterChange(
      newCondition,
      filter.type === 'number' && newCondition === 'equals' && activeCondition !== 'equals'
        ? ''
        : currentValue,
    );
    setActiveCondition(newCondition);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (filter.type === 'number' && value) {
      value = value.replaceAll(' ', '');
      let isValid = true;
      if (activeCondition === 'equals') {
        isValid = /^[0-9]+$/.test(value);
      } else {
        isValid = /^(([0-9](,)?)*)+$/.test(value);
      }

      isValid && onFilterChange(activeCondition, value);
    } else {
      onFilterChange(activeCondition, value);
    }
  };

  const value = activeFilters[activeCondition] || '';

  return (
    <Box display="flex" flexDirection="column" className={classes.filterInput}>
      <Input
        autoFocus
        data-testid={`${filter.type}-filter`}
        fullWidth
        onChange={handleInputChange}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            onFilterChange(activeCondition, activeFilters[activeCondition]);
            onClose(e);
          }
        }}
        inputProps={{
          pattern: /^[0-9]+$/,
        }}
        placeholder={placeholder}
        value={value}
      />
      {filter.conditions?.length > 1 && (
        <FormControl component="fieldset" className={classes.conditionsFormControl}>
          <RadioGroup
            aria-label="condition"
            name="condition"
            value={activeCondition}
            onChange={handleConditionChange}
          >
            {filter.conditions.map(({ key, label }, index) => (
              <FormControlLabel
                key={`condition-${index}-radio-${key}`}
                value={key}
                control={<Radio color={radioButtonColor} size="small" />}
                label={label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
    </Box>
  );
};
